import { Box, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { type DateTime, Interval } from "luxon";
import type { FC } from "react";
import { useTranslation } from "react-i18next";
import { INK, NEUTRAL, STATUS } from "../../theme/tokens";
import { type SelfSchedulingDayStructure, SelfSchedulingPreference } from "../../utils/models";
import SelfSchedulingDay from "./SelfSchedulingDay";
import useSelfSchedulingCalendarState from "./state/useSelfSchedulingCalendarState";

export type SelfScheduleCalendarDaysData = { [dateISO: string]: SelfSchedulingDayStructure };

type Props = {
	sundayIsFirstDayOfWeek?: boolean;
	onDayClick?: (date: DateTime, updatedPreference: SelfSchedulingPreference) => void;
	maxWeekCount?: number;
	daysData: SelfScheduleCalendarDaysData;
	cellWidth?: string;
	cellHeight?: string;
	cellWidthXS?: string;
	cellHeightXS?: string;
	borderColor?: string;
	targetWorkingDaysPerWeek?: number;
	overtimeWarningColor?: string;
	overtimeWarningColorSecondary?: string;
};

/**
 * SelfSchedulingCalendar component displays a calendar for self-scheduling preferences.
 * @param daysData - A record mapping date strings to SelfSchedulingDayStructure objects.
 * @param maxWeekCount - Maximum number of weeks to display in the calendar.
 * @param sundayIsFirstDayOfWeek - Boolean indicating if Sunday is the first day of the week.
 * @param onDayClick - Callback function triggered when a day is clicked, receiving the date and updated preference.
 * @param cellWidth - Width of each calendar cell, default is "50px".
 * @param cellHeight - Height of each calendar cell, default is "65px".
 * @param borderColor - Color of the cell borders, default is "#F1F3F9".
 * @param targetWorkingDaysPerWeek - Target number of working days per week, default is 3, above which a warning may be displayed.
 * @param overtimeWarningColor - Color for the overtime warning, default is "#E71C00".
 * @param overtimeWarningColorSecondary - Secondary color for the overtime warning, default is "#E71C001A".
 * @param cellWidthXS - Width of each calendar cell for extra small screens, default is "30px".
 * @param cellHeightXS - Height of each calendar cell for extra small screens, default is "40px".
 */
const SelfSchedulingCalendar: FC<Props> = ({
	daysData,
	maxWeekCount = 6,
	sundayIsFirstDayOfWeek = false,
	onDayClick,
	cellWidth = "50px",
	cellHeight = "65px",
	borderColor = NEUTRAL[150],
	targetWorkingDaysPerWeek = 3,
	overtimeWarningColorSecondary = alpha(STATUS.ERROR.main, 0.1),
	overtimeWarningColor = STATUS.ERROR.main,
	cellWidthXS = "45px",
	cellHeightXS = "58px",
}) => {
	const { t } = useTranslation();
	const { firstDate, calendarLabel, handleDayClick, amountOfDaysInWeek, firstDayOfWeeksWithOvertimeWarning } = useSelfSchedulingCalendarState({
		targetWorkingDaysPerWeek,
		daysData,
		maxWeekCount,
		sundayIsFirstDayOfWeek,
		onDayClick,
	});

	return (
		<Box
			sx={{
				display: "flex",
				flexDirection: "column",
				alignItems: "center",
				justifyContent: "start",
				width: "100%",
				height: "fit-content",
			}}
		>
			<Typography sx={{ fontWeight: 600, fontSize: "16px", lineHeight: "20px", userSelect: "none" }}>{calendarLabel}</Typography>
			<Box
				sx={{
					display: "flex",
					flexDirection: "column",
					width: "100%",
					height: "fit-content",
					justifyContent: "center",
					alignItems: "center",
					mt: 2,
				}}
			>
				<Box
					sx={{
						display: "flex",
						flexDirection: "row",
						width: "fit-content",
						justifyContent: "center",
					}}
				>
					{Array.from({ length: amountOfDaysInWeek }, (_, i) => {
						const currentDate = firstDate.plus({ day: i });
						return (
							<Typography
								key={currentDate.toISO()}
								align={"center"}
								style={{
									fontWeight: 600,
									fontSize: "12px",
									lineHeight: "100%",
									color: alpha(INK.SECONDARY, 0.5),
									userSelect: "none",
								}}
								sx={{
									width: {
										xs: cellWidthXS,
										mediumPhone: cellWidth,
									},
								}}
							>
								{currentDate.toFormat("ccc")}
							</Typography>
						);
					})}
				</Box>
				<Box sx={{ display: "flex", flexDirection: "row", alignItems: "center", justifyContent: "center", width: "fit-content", mt: 1 }}>
					{Array.from({ length: amountOfDaysInWeek }, (_, dayIndex) => {
						const daysInWeek = firstDate.plus({ days: dayIndex });
						return (
							<Box
								key={daysInWeek.toISO()}
								sx={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", width: "fit-content" }}
							>
								{Array.from({ length: maxWeekCount }, (_, weekIndex) => {
									const currentDate = daysInWeek.plus({ weeks: weekIndex });
									const formattedDate = currentDate.toISO() as string;
									const specificDateData = daysData[formattedDate] ?? {
										dayPreference: SelfSchedulingPreference.UNDEFINED,
										date: currentDate,
									};

									const markOvertimeWeek =
										/*We check if the current date is related to any overtime warning week - if so we should mark it!*/
										firstDayOfWeeksWithOvertimeWarning.some((firstDayOfWeekOfWarning) => {
											// Check for null/undefined before accessing properties
											if (!firstDayOfWeekOfWarning) return false;

											const lastDayOfWeekOfWarning = firstDayOfWeekOfWarning.plus({ days: 6 });

											// Luxon's Interval.fromDateTimes can be used for "isBetween" functionality
											const weekInterval = Interval.fromDateTimes(firstDayOfWeekOfWarning, lastDayOfWeekOfWarning);
											return currentDate.hasSame(firstDayOfWeekOfWarning, "day") || weekInterval.contains(currentDate);
										});

									return (
										<Box
											key={formattedDate}
											style={{
												display: "flex",
												alignItems: "center",
												justifyContent: "center",
												borderLeft: `1px solid ${borderColor}`,
												borderTop: `1px solid ${borderColor}`,
												borderBottom: `1px solid ${weekIndex === maxWeekCount - 1 ? borderColor : "transparent"}`,
												borderRight: `1px solid ${dayIndex === amountOfDaysInWeek - 1 ? borderColor : "transparent"}`,
												background: markOvertimeWeek ? overtimeWarningColorSecondary : "transparent",
											}}
											sx={{
												width: { xs: cellWidthXS, mediumPhone: cellWidth },
												height: { xs: cellHeightXS, mediumPhone: cellHeight },
											}}
										>
											<SelfSchedulingDay key={`${formattedDate}-${specificDateData.dayPreference}`} dayData={specificDateData} onClick={handleDayClick} />
										</Box>
									);
								})}
							</Box>
						);
					})}
				</Box>
				{firstDayOfWeeksWithOvertimeWarning && firstDayOfWeeksWithOvertimeWarning.length > 0 && (
					<Box
						sx={{
							mt: 2,
							display: "flex",
							alignItems: "center",
							justifyContent: "center",
							width: "100%",
						}}
					>
						<img src="/Info.svg" alt="" />
						<Typography
							color={overtimeWarningColor}
							sx={{
								ml: 1,
								fontSize: "13px",
							}}
						>
							{t("signup.overtime.warning", { targetWorkingDaysPerWeek })}
						</Typography>
					</Box>
				)}
			</Box>
		</Box>
	);
};

export default SelfSchedulingCalendar;
